import { Injectable, computed, effect, signal } from '@angular/core';
import { Observable, asyncScheduler, firstValueFrom, scheduled, throwError } from 'rxjs';
import { catchError, finalize, shareReplay, tap } from 'rxjs/operators';
import {
  WEBSITE_BUILDER_SECTION_PRESETS,
  WebsiteBuilderDraftState,
  WebsiteBuilderHoverEffect,
  WebsiteBuilderNode,
  WebsiteBuilderNodeContent,
  WebsiteBuilderStyleMode,
  WebsitePageDocument,
  buildWebsiteBuilderNodeStyles,
  cloneComponentDefinitionIntoPageNode,
  collectWebsiteBuilderDropListIds,
  createComponentDefinitionFromPageNode,
  createWebsiteBuilderSectionPreset,
  findWebsiteBuilderNode,
  findWebsiteBuilderParentNode,
  isWebsiteBuilderContainerNode,
  normalizeWebsitePageDocument,
} from '../models/website-builder.models';
import { PageRevisionSummary, WebsiteDataCollection, WebsitePageSummary, WebsiteSummary } from '../models/website-management.models';
import { WebsiteBuilderApiError, WebsiteBuilderApiService } from './website-builder-api.service';
import { ComponentStudioService } from './component-studio.service';
import { StudioComponentDefinition, StudioNodeAnimation, StudioNodeStyle, StudioNodeTransform } from '../models/component-studio.models';
import { createPrefixedUuidV7 } from '../utils/public-id.utils';
import { UndoRedoHistory } from '../utils/undo-redo-history';

interface BuilderContext {
  websitePublicId: string;
  pagePublicId: string;
}

interface WebsiteBuilderHistorySnapshot {
  document: WebsitePageDocument;
  selectedNodeId: string | null;
}

@Injectable({
  providedIn: 'root'
})
export class WebsiteBuilderService {
  private readonly pendingDraftStorageKeyPrefix = 'bw.website-builder.pending';
  private readonly saveIdleDelayMs = 10000;
  private readonly saveMaxDelayMs = 60000;
  private readonly websiteSignal = signal<WebsiteSummary | null>(null);
  private readonly pageSignal = signal<WebsitePageSummary | null>(null);
  private readonly pagesSignal = signal<WebsitePageSummary[]>([]);
  private readonly dataCollectionsSignal = signal<WebsiteDataCollection[]>([]);
  private readonly draftRevisionSignal = signal<PageRevisionSummary | null>(null);
  private readonly documentSignal = signal<WebsitePageDocument | null>(null);
  private readonly selectedNodeIdSignal = signal<string | null>(null);
  private readonly loadedSignal = signal(false);
  private readonly loadingSignal = signal(false);
  private readonly loadErrorSignal = signal<string | null>(null);
  private readonly savingDraftSignal = signal(false);
  private readonly saveErrorSignal = signal<string | null>(null);
  private readonly canUndoSignal = signal(false);
  private readonly canRedoSignal = signal(false);
  private readonly hasPendingChangesSignal = signal(false);

  private loadRequest$: Observable<WebsiteBuilderDraftState> | null = null;
  private currentContext: BuilderContext | null = null;
  private readonly history = new UndoRedoHistory<WebsiteBuilderHistorySnapshot>(
    (snapshot) => JSON.stringify(snapshot)
  );
  private saveTimer: ReturnType<typeof setTimeout> | null = null;
  private maxSaveTimer: ReturnType<typeof setTimeout> | null = null;
  private dirtySince: number | null = null;
  private lastSavedSerialized = '';
  private savePromise: Promise<void> | null = null;

  readonly website = computed(() => this.websiteSignal());
  readonly page = computed(() => this.pageSignal());
  readonly pages = computed(() => this.pagesSignal());
  readonly dataCollections = computed(() => this.dataCollectionsSignal());
  readonly draftRevision = computed(() => this.draftRevisionSignal());
  readonly document = computed(() => this.documentSignal());
  readonly selectedNodeId = computed(() => this.selectedNodeIdSignal());
  readonly selectedNode = computed<WebsiteBuilderNode | null>(() => {
    const document = this.documentSignal();
    if (!document) {
      return null;
    }

    const selectedNodeId = this.selectedNodeIdSignal() ?? document.root.id;
    return findWebsiteBuilderNode(document.root, selectedNodeId) ?? document.root;
  });
  readonly selectedParentNode = computed<WebsiteBuilderNode | null>(() => {
    const document = this.documentSignal();
    const selectedNode = this.selectedNode();

    if (!document || !selectedNode) {
      return null;
    }

    return findWebsiteBuilderParentNode(document.root, selectedNode.id);
  });
  readonly dropListIds = computed(() => {
    const document = this.documentSignal();
    return document ? collectWebsiteBuilderDropListIds(document.root) : [];
  });
  readonly isLoaded = computed(() => this.loadedSignal());
  readonly isLoading = computed(() => this.loadingSignal());
  readonly loadError = computed(() => this.loadErrorSignal());
  readonly isSavingDraft = computed(() => this.savingDraftSignal());
  readonly saveError = computed(() => this.saveErrorSignal());
  readonly canUndo = computed(() => this.canUndoSignal());
  readonly canRedo = computed(() => this.canRedoSignal());
  readonly hasPendingChanges = computed(() => this.hasPendingChangesSignal());
  readonly sectionPresets = WEBSITE_BUILDER_SECTION_PRESETS;

  constructor(
    private readonly websiteBuilderApiService: WebsiteBuilderApiService,
    private readonly componentStudioService: ComponentStudioService
  ) {
    effect(() => {
      const document = this.documentSignal();
      if (!document) {
        this.selectedNodeIdSignal.set(null);
        return;
      }

      const selectedNodeId = this.selectedNodeIdSignal();
      if (!selectedNodeId || !findWebsiteBuilderNode(document.root, selectedNodeId)) {
        this.selectedNodeIdSignal.set(document.root.id);
      }
    });
  }

  loadBuilder(websitePublicId: string, pagePublicId: string, force = false): Observable<WebsiteBuilderDraftState> {
    const sameContext =
      this.currentContext?.websitePublicId === websitePublicId &&
      this.currentContext?.pagePublicId === pagePublicId;

    if (this.loadedSignal() && sameContext && !force) {
      return scheduled([this.snapshotState()], asyncScheduler);
    }

    if (this.loadRequest$ && sameContext && !force) {
      return this.loadRequest$;
    }

    this.currentContext = { websitePublicId, pagePublicId };
    this.loadedSignal.set(false);
    this.loadingSignal.set(true);
    this.loadErrorSignal.set(null);

    const request$ = this.websiteBuilderApiService.getDraft(websitePublicId, pagePublicId).pipe(
      tap((state) => {
        this.applyLoadedState(state);
        this.loadedSignal.set(true);
        this.lastSavedSerialized = this.serializePersistedDocument(this.documentSignal());
        this.restorePendingDraft();
        this.history.clear();
        this.syncHistorySignals();
      }),
      catchError((error) => {
        this.loadErrorSignal.set(this.resolveErrorMessage(error));
        return throwError(() => error);
      }),
      finalize(() => {
        this.loadingSignal.set(false);
        this.loadRequest$ = null;
      }),
      shareReplay(1)
    );

    this.loadRequest$ = request$;
    return request$;
  }

  selectNode(nodeId: string | null): void {
    const document = this.documentSignal();
    if (!document) {
      this.selectedNodeIdSignal.set(null);
      return;
    }

    if (!nodeId || !findWebsiteBuilderNode(document.root, nodeId)) {
      this.selectedNodeIdSignal.set(document.root.id);
      return;
    }

    this.selectedNodeIdSignal.set(nodeId);
  }

  updateSelectedNodeLabel(label: string): void {
    this.patchSelectedNode((node) => {
      node.label = label.trim() || node.label;
    });
  }

  updateSelectedNodeContent(contentPatch: Partial<WebsiteBuilderNodeContent>): void {
    const shouldPatchRepeatPeers = Object.keys(contentPatch).some((field) =>
      REPEAT_PEER_CONTENT_FIELDS.has(field as keyof WebsiteBuilderNodeContent)
    );

    this.patchSelectedNode((node) => {
      node.content = {
        ...node.content,
        ...contentPatch
      };
    }, true, shouldPatchRepeatPeers);
  }

  setSelectedNodeInternalTarget(targetPagePublicId: string | null): void {
    this.patchSelectedNode((node) => {
      node.content = {
        ...node.content,
        linkKind: targetPagePublicId ? 'internal-page' : 'none',
        targetPagePublicId,
        href: targetPagePublicId ? '' : node.content.href,
        openInNewTab: targetPagePublicId ? false : node.content.openInNewTab
      };
    });
  }

  setSelectedNodeLinkKind(linkKind: WebsiteBuilderNodeContent['linkKind']): void {
    this.patchSelectedNode((node) => {
      node.content = {
        ...node.content,
        linkKind: linkKind ?? 'none',
        href: linkKind === 'external-url' ? (node.content.href || '') : '',
        openInNewTab: linkKind === 'none' ? false : node.content.openInNewTab ?? false,
        targetPagePublicId:
          linkKind === 'internal-page'
            ? node.content.targetPagePublicId ?? this.getFallbackInternalTarget()
            : null
      };
    });
  }

  setSelectedNodeOpenInNewTab(openInNewTab: boolean): void {
    this.patchSelectedNode((node) => {
      node.content = {
        ...node.content,
        openInNewTab
      };
    });
  }

  setSelectedNodeHoverEffect(hoverEffect: WebsiteBuilderHoverEffect): void {
    this.patchSelectedNode((node) => {
      node.content = {
        ...node.content,
        hoverEffect
      };
    }, true, true);
  }

  updateSelectedNodeStyle(stylePatch: Partial<StudioNodeStyle>, captureHistory = true): void {
    this.patchSelectedNode((node) => {
      node.style = {
        ...node.style,
        ...stylePatch
      };
    }, captureHistory, true);
  }

  updateSelectedNodeStyleVariant(
    packageId: string,
    mode: WebsiteBuilderStyleMode,
    stylePatch: Partial<StudioNodeStyle>,
    captureHistory = true
  ): void {
    this.patchSelectedNode((node) => {
      const currentPackage = node.styleVariants?.[packageId] ?? {};
      const currentMode = currentPackage[mode] ?? {};
      const nextMode: Partial<StudioNodeStyle> = {
        ...currentMode,
        ...stylePatch
      };

      if (stylePatch.transform) {
        nextMode.transform = {
          ...(currentMode.transform ?? {}),
          ...stylePatch.transform
        } as StudioNodeStyle['transform'];
      }

      if (stylePatch.animation) {
        nextMode.animation = {
          ...(currentMode.animation ?? {}),
          ...stylePatch.animation
        } as StudioNodeStyle['animation'];
      }

      node.styleVariants = {
        ...(node.styleVariants ?? {}),
        [packageId]: {
          ...currentPackage,
          [mode]: nextMode
        }
      };
    }, captureHistory, true);
  }

  updateSelectedNodeTransform(transformPatch: Partial<StudioNodeTransform>, captureHistory = true): void {
    this.patchSelectedNode((node) => {
      node.style = {
        ...node.style,
        transform: {
          ...node.style.transform,
          ...transformPatch
        }
      };
    }, captureHistory, true);
  }

  updateSelectedNodeAnimation(animationPatch: Partial<StudioNodeAnimation>): void {
    this.patchSelectedNode((node) => {
      node.style = {
        ...node.style,
        animation: {
          ...node.style.animation,
          ...animationPatch
        }
      };
    }, true, true);
  }

  toggleSelectedHidden(): void {
    this.patchSelectedNode((node) => {
      node.hidden = !node.hidden;
    });
  }

  toggleSelectedLocked(): void {
    this.patchSelectedNode((node) => {
      node.locked = !node.locked;
    });
  }

  duplicateSelectedNode(): void {
    const selectedNode = this.selectedNode();
    const selectedParent = this.selectedParentNode();

    if (!selectedNode || !selectedParent) {
      return;
    }

    const duplicateNode = structuredClone(selectedNode);
    refreshNodeIds(duplicateNode);

    this.patchDocument((document) => {
      const parent = findWebsiteBuilderNode(document.root, selectedParent.id);
      if (!parent) {
        return;
      }

      const insertIndex = parent.children.findIndex((child) => child.id === selectedNode.id);
      parent.children.splice(insertIndex + 1, 0, duplicateNode);
    });

    this.selectedNodeIdSignal.set(duplicateNode.id);
  }

  removeSelectedNode(): void {
    const selectedNode = this.selectedNode();
    const selectedParent = this.selectedParentNode();

    if (!selectedNode || !selectedParent) {
      return;
    }

    this.patchDocument((document) => {
      const parent = findWebsiteBuilderNode(document.root, selectedParent.id);
      if (!parent) {
        return;
      }

      parent.children = parent.children.filter((child) => child.id !== selectedNode.id);
    });

    this.selectedNodeIdSignal.set(selectedParent.id);
  }

  insertSectionPreset(presetId: string, explicitParentId?: string | null): string | null {
    const document = this.documentSignal();
    if (!document) {
      return null;
    }

    const targetParentId = explicitParentId ?? this.resolveInsertionParentId(document.root);
    const targetParent = findWebsiteBuilderNode(document.root, targetParentId);
    if (!targetParent || !isWebsiteBuilderContainerNode(targetParent)) {
      return null;
    }

    const newNode = createWebsiteBuilderSectionPreset(presetId);

    this.patchDocument((draftDocument) => {
      const parent = findWebsiteBuilderNode(draftDocument.root, targetParentId);
      if (!parent) {
        return;
      }

      parent.children.push(newNode);
    });

    this.selectedNodeIdSignal.set(newNode.id);
    return newNode.id;
  }

  insertComponentDefinition(definition: StudioComponentDefinition, explicitParentId?: string | null): string | null {
    const document = this.documentSignal();
    if (!document) {
      return null;
    }

    const targetParentId = explicitParentId ?? this.resolveInsertionParentId(document.root);
    const targetParent = findWebsiteBuilderNode(document.root, targetParentId);
    if (!targetParent || !isWebsiteBuilderContainerNode(targetParent)) {
      return null;
    }

    const componentNode = cloneComponentDefinitionIntoPageNode(definition);

    this.patchDocument((draftDocument) => {
      const parent = findWebsiteBuilderNode(draftDocument.root, targetParentId);
      if (!parent) {
        return;
      }

      parent.children.push(componentNode);
    });

    this.selectedNodeIdSignal.set(componentNode.id);
    return componentNode.id;
  }

  moveNode(nodeId: string, targetParentId: string, targetIndex: number): void {
    const document = this.documentSignal();
    if (!document || document.root.id === nodeId) {
      return;
    }

    const sourceParent = findWebsiteBuilderParentNode(document.root, nodeId);
    const targetParent = findWebsiteBuilderNode(document.root, targetParentId);
    const draggedNode = findWebsiteBuilderNode(document.root, nodeId);

    if (!sourceParent || !targetParent || !draggedNode || !isWebsiteBuilderContainerNode(targetParent)) {
      return;
    }

    if (draggedNode.id === targetParentId || findWebsiteBuilderNode(draggedNode, targetParentId)) {
      return;
    }

    this.patchDocument((draftDocument) => {
      const draftSourceParent = findWebsiteBuilderParentNode(draftDocument.root, nodeId);
      const draftTargetParent = findWebsiteBuilderNode(draftDocument.root, targetParentId);
      if (!draftSourceParent || !draftTargetParent || !isWebsiteBuilderContainerNode(draftTargetParent)) {
        return;
      }

      const sourceIndex = draftSourceParent.children.findIndex((child) => child.id === nodeId);
      if (sourceIndex === -1) {
        return;
      }

      const [movedNode] = draftSourceParent.children.splice(sourceIndex, 1);
      const clampedIndex = Math.max(0, Math.min(targetIndex, draftTargetParent.children.length));
      draftTargetParent.children.splice(clampedIndex, 0, movedNode);
    });

    this.selectedNodeIdSignal.set(nodeId);
  }

  undo(): boolean {
    const snapshot = this.history.undo(this.captureHistorySnapshot());
    if (!snapshot) {
      return false;
    }

    this.applyHistorySnapshot(snapshot);
    this.syncHistorySignals();
    this.markStateChanged(false);
    return true;
  }

  redo(): boolean {
    const snapshot = this.history.redo(this.captureHistorySnapshot());
    if (!snapshot) {
      return false;
    }

    this.applyHistorySnapshot(snapshot);
    this.syncHistorySignals();
    this.markStateChanged(false);
    return true;
  }

  beginHistoryTransaction(): void {
    this.captureUndoSnapshot();
  }

  async flushPendingSave(): Promise<void> {
    if (!this.loadedSignal()) {
      return;
    }

    this.clearScheduledSave();

    while (true) {
      if (this.savePromise) {
        await this.savePromise;
        continue;
      }

      if (!this.hasPersistedStateChanges()) {
        this.hasPendingChangesSignal.set(false);
        this.clearPendingDraftStorage();
        this.dirtySince = null;
        return;
      }

      await this.performRemoteSave();
    }
  }

  flushPendingSaveOnUnload(): void {
    const context = this.currentContext;
    const document = this.documentSignal();

    if (!this.loadedSignal() || !context || !document || !this.hasPersistedStateChanges()) {
      return;
    }

    this.persistPendingDraft();
    this.websiteBuilderApiService.updateDraftKeepalive(
      context.websitePublicId,
      context.pagePublicId,
      structuredClone(document)
    );
  }

  saveSelectedNodeAsComponent(componentName?: string): string | null {
    const selectedNode = this.selectedNode();
    if (!selectedNode || selectedNode.nodeRole === 'page-root') {
      return null;
    }

    const definition = createComponentDefinitionFromPageNode(selectedNode, componentName ?? selectedNode.label);
    return this.componentStudioService.registerExternalDefinition(definition);
  }

  buildNodeStyles(
    node: WebsiteBuilderNode,
    packageId?: string,
    mode?: WebsiteBuilderStyleMode
  ): Record<string, string | number> {
    return buildWebsiteBuilderNodeStyles(node, packageId, mode);
  }

  canRemoveSelected(node: WebsiteBuilderNode | null): boolean {
    return !!node && node.nodeRole !== 'page-root' && !!this.selectedParentNode();
  }

  canSaveSelectedAsComponent(node: WebsiteBuilderNode | null): boolean {
    return !!node && node.nodeRole !== 'page-root';
  }

  private applyLoadedState(state: WebsiteBuilderDraftState, preserveSelection = false): void {
    const previousSelectedNodeId = preserveSelection ? this.selectedNodeIdSignal() : null;
    const normalizedDocument = normalizeWebsitePageDocument(state.document);

    this.websiteSignal.set(state.website);
    this.pageSignal.set(state.page);
    this.pagesSignal.set(state.pages);
    this.dataCollectionsSignal.set(state.dataCollections ?? []);
    this.draftRevisionSignal.set(state.draftRevision);
    this.documentSignal.set(normalizedDocument);

    if (previousSelectedNodeId && findWebsiteBuilderNode(normalizedDocument.root, previousSelectedNodeId)) {
      this.selectedNodeIdSignal.set(previousSelectedNodeId);
    } else {
      this.selectedNodeIdSignal.set(normalizedDocument.root.id);
    }
  }

  private snapshotState(): WebsiteBuilderDraftState {
    return {
      website: structuredClone(this.websiteSignal() as WebsiteSummary),
      page: structuredClone(this.pageSignal() as WebsitePageSummary),
      pages: structuredClone(this.pagesSignal()),
      dataCollections: structuredClone(this.dataCollectionsSignal()),
      draftRevision: structuredClone(this.draftRevisionSignal()),
      document: structuredClone(this.documentSignal() as WebsitePageDocument)
    };
  }

  private patchSelectedNode(
    mutateNode: (node: WebsiteBuilderNode) => void,
    captureHistory = true,
    patchRepeatPeers = false
  ): void {
    const selectedNode = this.selectedNode();
    if (!selectedNode) {
      return;
    }

    const repeatTemplateKey = selectedNode.content.repeatTemplateKey;
    const repeatPartKey = selectedNode.content.repeatPartKey;

    this.patchDocument((document) => {
      const targetNode = findWebsiteBuilderNode(document.root, selectedNode.id);
      if (!targetNode) {
        return;
      }

      mutateNode(targetNode);
      if (patchRepeatPeers && repeatTemplateKey && repeatPartKey) {
        applyToRepeatPeers(document.root, selectedNode.id, repeatTemplateKey, repeatPartKey, mutateNode);
      }
    }, captureHistory);
  }

  private patchDocument(mutateDocument: (document: WebsitePageDocument) => void, captureHistory = true): void {
    const document = this.documentSignal();
    if (!document) {
      return;
    }

    const draftDocument = structuredClone(document);
    mutateDocument(draftDocument);
    if (JSON.stringify(draftDocument) === JSON.stringify(document)) {
      return;
    }

    if (captureHistory) {
      this.captureUndoSnapshot();
    }
    draftDocument.updatedAt = new Date().toISOString();
    this.documentSignal.set(draftDocument);
    this.markStateChanged();
  }

  private resolveInsertionParentId(rootNode: WebsiteBuilderNode): string {
    const selectedNode = this.selectedNode();
    if (!selectedNode) {
      return rootNode.id;
    }

    if (isWebsiteBuilderContainerNode(selectedNode)) {
      return selectedNode.id;
    }

    return findWebsiteBuilderParentNode(rootNode, selectedNode.id)?.id ?? rootNode.id;
  }

  private getFallbackInternalTarget(): string | null {
    const currentPagePublicId = this.pageSignal()?.publicId ?? null;
    return this.pagesSignal().find((page) => page.publicId !== currentPagePublicId)?.publicId ?? null;
  }

  private resolveErrorMessage(error: unknown): string {
    const apiError = error as WebsiteBuilderApiError | undefined;
    return apiError?.message || 'An unexpected error occurred';
  }

  private captureUndoSnapshot(): void {
    this.history.capture(this.captureHistorySnapshot());
    this.syncHistorySignals();
  }

  private captureHistorySnapshot(): WebsiteBuilderHistorySnapshot {
    return {
      document: structuredClone(this.documentSignal() as WebsitePageDocument),
      selectedNodeId: this.selectedNodeIdSignal()
    };
  }

  private applyHistorySnapshot(snapshot: WebsiteBuilderHistorySnapshot): void {
    const normalizedDocument = normalizeWebsitePageDocument(snapshot.document);
    this.documentSignal.set(normalizedDocument);
    const selectedNodeId = snapshot.selectedNodeId && findWebsiteBuilderNode(normalizedDocument.root, snapshot.selectedNodeId)
      ? snapshot.selectedNodeId
      : normalizedDocument.root.id;

    this.selectedNodeIdSignal.set(selectedNodeId);
  }

  private markStateChanged(captureHistory = false): void {
    if (!this.loadedSignal()) {
      return;
    }

    if (captureHistory) {
      this.captureUndoSnapshot();
    }

    if (!this.hasPersistedStateChanges()) {
      this.hasPendingChangesSignal.set(false);
      this.clearPendingDraftStorage();
      this.clearScheduledSave();
      this.dirtySince = null;
      return;
    }

    this.hasPendingChangesSignal.set(true);
    this.persistPendingDraft();
    this.scheduleBufferedSave();
  }

  private scheduleBufferedSave(): void {
    if (!this.loadedSignal() || !this.hasPersistedStateChanges()) {
      return;
    }

    if (this.dirtySince === null) {
      this.dirtySince = Date.now();
    }

    if (this.saveTimer) {
      clearTimeout(this.saveTimer);
    }

    this.saveTimer = setTimeout(() => {
      void this.performRemoteSave();
    }, this.saveIdleDelayMs);

    if (!this.maxSaveTimer && this.dirtySince !== null) {
      const elapsed = Date.now() - this.dirtySince;
      const remaining = Math.max(0, this.saveMaxDelayMs - elapsed);
      this.maxSaveTimer = setTimeout(() => {
        void this.performRemoteSave();
      }, remaining);
    }
  }

  private clearScheduledSave(): void {
    if (this.saveTimer) {
      clearTimeout(this.saveTimer);
      this.saveTimer = null;
    }

    if (this.maxSaveTimer) {
      clearTimeout(this.maxSaveTimer);
      this.maxSaveTimer = null;
    }
  }

  private async performRemoteSave(): Promise<void> {
    const context = this.currentContext;
    const document = this.documentSignal();

    if (!this.loadedSignal() || !context || !document || !this.hasPersistedStateChanges()) {
      return;
    }

    if (this.savePromise) {
      return this.savePromise;
    }

    this.clearScheduledSave();
    this.savingDraftSignal.set(true);
    this.saveErrorSignal.set(null);

    const payload = structuredClone(document);
    const requestSerialized = this.serializePersistedDocument(payload);

    this.savePromise = firstValueFrom(
      this.websiteBuilderApiService.updateDraft(context.websitePublicId, context.pagePublicId, payload)
    )
      .then((state) => {
        const currentSerialized = this.serializePersistedDocument();
        const savedSerialized = this.serializePersistedDocument(state.document);
        const shouldApplySavedState = currentSerialized === requestSerialized;

        this.websiteSignal.set(state.website);
        this.pageSignal.set(state.page);
        this.pagesSignal.set(state.pages);
        this.dataCollectionsSignal.set(state.dataCollections ?? []);
        this.draftRevisionSignal.set(state.draftRevision);

        if (shouldApplySavedState) {
          this.applyLoadedState(state, true);
        }

        this.lastSavedSerialized = shouldApplySavedState
          ? this.serializePersistedDocument()
          : savedSerialized;

        if (this.hasPersistedStateChanges()) {
          this.hasPendingChangesSignal.set(true);
          this.persistPendingDraft();
          this.dirtySince = Date.now();
          this.scheduleBufferedSave();
          return;
        }

        this.hasPendingChangesSignal.set(false);
        this.clearPendingDraftStorage();
        this.dirtySince = null;
      })
      .catch((error: unknown) => {
        this.saveErrorSignal.set(this.resolveErrorMessage(error));
        this.hasPendingChangesSignal.set(this.hasPersistedStateChanges());
        this.persistPendingDraft();
        this.dirtySince ??= Date.now();
        this.scheduleBufferedSave();
      })
      .finally(() => {
        this.savingDraftSignal.set(false);
        this.savePromise = null;
      });

    return this.savePromise;
  }

  private hasPersistedStateChanges(): boolean {
    return this.serializePersistedDocument() !== this.lastSavedSerialized;
  }

  private serializePersistedDocument(document = this.documentSignal()): string {
    if (!document) {
      return '';
    }

    return JSON.stringify(this.buildComparableDocument(document));
  }

  private buildComparableDocument(document: WebsitePageDocument): Omit<WebsitePageDocument, 'updatedAt'> {
    const normalizedDocument = normalizeWebsitePageDocument(document);
    const { updatedAt: _updatedAt, ...comparableDocument } = normalizedDocument;

    return comparableDocument;
  }

  private restorePendingDraft(): void {
    if (typeof window === 'undefined' || !this.currentContext) {
      return;
    }

    try {
      const rawDraft = window.localStorage.getItem(this.pendingStorageKey(this.currentContext));
      if (!rawDraft) {
        return;
      }

      const parsedDraft = JSON.parse(rawDraft) as WebsiteBuilderHistorySnapshot;
      if (!parsedDraft?.document?.root) {
        return;
      }

      this.applyHistorySnapshot({
        document: parsedDraft.document,
        selectedNodeId: parsedDraft.selectedNodeId ?? null
      });

      if (!this.hasPersistedStateChanges()) {
        this.clearPendingDraftStorage();
        return;
      }

      this.hasPendingChangesSignal.set(true);
      this.dirtySince = Date.now();
      this.scheduleBufferedSave();
    } catch {
      // Ignore malformed local drafts and continue with server state.
    }
  }

  private persistPendingDraft(): void {
    if (typeof window === 'undefined' || !this.currentContext) {
      return;
    }

    window.localStorage.setItem(this.pendingStorageKey(this.currentContext), JSON.stringify(this.captureHistorySnapshot()));
  }

  private clearPendingDraftStorage(): void {
    if (typeof window === 'undefined' || !this.currentContext) {
      return;
    }

    window.localStorage.removeItem(this.pendingStorageKey(this.currentContext));
  }

  private pendingStorageKey(context: BuilderContext): string {
    return `${this.pendingDraftStorageKeyPrefix}.${context.websitePublicId}.${context.pagePublicId}`;
  }

  private syncHistorySignals(): void {
    this.canUndoSignal.set(this.history.canUndo);
    this.canRedoSignal.set(this.history.canRedo);
  }
}

function refreshNodeIds(node: WebsiteBuilderNode): void {
  node.id = createNodeId();
  node.children.forEach((child) => refreshNodeIds(child));
}

function applyToRepeatPeers(
  node: WebsiteBuilderNode,
  selectedNodeId: string,
  repeatTemplateKey: string,
  repeatPartKey: string,
  mutateNode: (node: WebsiteBuilderNode) => void
): void {
  if (
    node.id !== selectedNodeId &&
    node.content.repeatTemplateKey === repeatTemplateKey &&
    node.content.repeatPartKey === repeatPartKey
  ) {
    mutateNode(node);
  }

  node.children.forEach((child) =>
    applyToRepeatPeers(child, selectedNodeId, repeatTemplateKey, repeatPartKey, mutateNode)
  );
}

const REPEAT_PEER_CONTENT_FIELDS = new Set<keyof WebsiteBuilderNodeContent>([
  'dynamicComponent',
  'dataCollectionKey',
  'dataItemAlias',
  'dataItemLimit',
  'dataBinding',
  'dataAction',
  'commerceSlot',
  'commerceBinding',
  'commerceAction',
  'repeatTemplateKey',
  'repeatPartKey',
  'hoverEffect'
]);

function createNodeId(): string {
  return createPrefixedUuidV7('node');
}
